import { Grid, Table, TableBody, TableCell, TableHead, TableRow, useMediaQuery } from '@rbx/ui';
import React, { Fragment, FunctionComponent } from 'react';
import { useTranslation } from '@rbx/intl';
import { useAppConfig } from '@modules/layout/layout/components/AppConfigProvider';
import useMarkdownTableStyles from '@modules/ui/markdown/components/MarkdownTable.styles';
import { EnumReferenceItem } from '../../types';
import useEnumCharacteristicsIteratorStyles from './EnumCharacteristicsIterator.styles';
import ReferenceMemberTags from '../../sharedUI/components/ReferenceMemberTags';
import APIReferenceDescription from '../../sharedUI/components/APIReferenceDescription';
import { Cx } from '../../../../../types';

interface EnumCharacteristicsIteratorProps {
  enumItems: EnumReferenceItem[];
}

interface IteratorRowProps {
  cx: Cx;
  enumItem: EnumReferenceItem;
  isMobile?: boolean;
  classes?: Record<string, string>;
}

const SummaryRow = ({
  enumItem: { name, summary, deprecationMessage, tags },
  classes,
}: {
  enumItem: EnumReferenceItem;
  classes?: Record<string, string>;
}) => {
  return (
    <React.Fragment>
      <APIReferenceDescription
        name={name}
        description={summary}
        deprecationMessage={deprecationMessage}
      />
      <div>
        {(tags?.length ?? 0) > 0 && (
          <Grid item XSmall>
            <ReferenceMemberTags className={classes?.tags} memberName={name} tags={tags || []} />
          </Grid>
        )}
      </div>
    </React.Fragment>
  );
};

// TODO: IteratorRow is implicitly required in many places.
// have better abstraction
const IteratorRow: FunctionComponent<IteratorRowProps> = ({ cx, enumItem, isMobile, classes }) => {
  const hasSummaryRow = isMobile && (!!enumItem.summary || !!enumItem.tags?.length);

  return (
    <Fragment>
      <TableRow
        className={cx(
          classes?.row || '',
          hasSummaryRow ? { [classes?.rowAboveSummaryRow || '']: true } : undefined,
        )}
        key={enumItem.name}
        id={enumItem.name}>
        <TableCell>{enumItem.name}</TableCell>
        <TableCell className={classes?.value}>{enumItem.value}</TableCell>
        {!isMobile && (
          <TableCell className={classes?.summary}>
            <SummaryRow enumItem={enumItem} classes={classes} />
          </TableCell>
        )}
      </TableRow>
      {hasSummaryRow && (
        <TableRow
          className={cx(classes?.row || '', {
            [classes?.summaryRow || '']: true,
            [classes?.hasSummary || '']: !!enumItem.summary,
          })}
          key={`${enumItem.name}-2`}>
          <TableCell colSpan={12} className={classes?.summary}>
            <SummaryRow enumItem={enumItem} classes={classes} />
          </TableCell>
        </TableRow>
      )}
    </Fragment>
  );
};

const EnumCharacteristicsIterator: FunctionComponent<
  React.PropsWithChildren<EnumCharacteristicsIteratorProps>
> = ({ enumItems }) => {
  const { translate } = useTranslation();
  const { enableEngineReferenceRevamp } = useAppConfig();
  const {
    classes: { outerContainer },
    cx,
  } = useMarkdownTableStyles();
  const { classes } = useEnumCharacteristicsIteratorStyles({
    enableEngineReferenceRevamp: !!enableEngineReferenceRevamp,
  });
  const isMobile = useMediaQuery((theme) => theme.breakpoints.down('Medium'));
  return (
    <Grid item className={outerContainer}>
      <Table stickyHeader>
        <TableHead>
          <TableRow className={classes?.header}>
            <TableCell>{translate('Label.Name') || 'Name'}</TableCell>
            <TableCell className={classes?.value}>{translate('Label.Value') || 'Value'}</TableCell>
            {!isMobile && <TableCell>{translate('Label.Summary') || 'Summary'}</TableCell>}
          </TableRow>
        </TableHead>
        <TableBody>
          {enumItems.map((enumItem) => (
            <IteratorRow
              key={enumItem.name}
              cx={cx}
              enumItem={enumItem}
              isMobile={isMobile}
              classes={classes}
            />
          ))}
        </TableBody>
      </Table>
    </Grid>
  );
};

export default EnumCharacteristicsIterator;
